import React, { useState } from 'react'
import toast from 'react-hot-toast'
import { useMutation } from 'react-query'
import { AxiosError } from 'axios'

import {
  Edit as EditIcon,
  Home as HomeIcon,
  Label as LabelIcon,
  Print as PrintIcon,
} from '@material-ui/icons'

import { useAsyncPrint } from 'src/components/ToPrint/useAsyncPrint'
import { OrderLabel } from 'src/modules/supply_chain/components/freight/OrderLabel'
import { PickTicket } from 'src/modules/supply_chain/components/freight/PickTicket'
import { FreightOrderDialog } from 'src/modules/supply_chain/components/freight/OrderDialog'
import { FreightOrder } from 'src/api/models'
import { handleErrorMessage, Notification } from 'src/components/Notification'
import {
  AddressField,
  DateField,
  DateTimeField,
  FlagsField,
  OrderStatusField,
  RelatedLinkedField,
  RelatedListValueField,
  ValueField,
} from 'src/components/Table'
import { skuit } from 'src/services/skuit'
import { DetailComponent } from './base'

interface ObjInfo {
  data: any
  isLoading: boolean
  refetch: () => void
}

interface DetailTableProps {
  obj: ObjInfo
  refresh?: () => void
  customFields?: Record<string, string>
}

export const FreightOrderDetailTable = (props: DetailTableProps) => {
  const { obj, refresh, customFields } = props
  const [updateOpen, setUpdateOpen] = useState(false)
  const { print: asyncPrint, renderPrintComponent: renderAsyncPrintComponent } = useAsyncPrint()

  const { mutate: generateRetailerLabels } = useMutation(
    skuit.sc.freight.orders.generateRetailerLabels,
    {
      onSuccess: () => {
        toast.success(() => <Notification message={'Successfully generated Retailer Labels.'} />)
      },
      onError: (error: AxiosError) => {
        handleErrorMessage(error, 'generate', 'Retailer Labels')
      },
    }
  )

  return (
    <>
      {renderAsyncPrintComponent()}
      {updateOpen && (
        <FreightOrderDialog
          open={updateOpen}
          order={obj.data}
          update
          handleClose={() => {
            setUpdateOpen(false)
          }}
          onSave={refresh}
        />
      )}
      <DetailComponent
        obj={obj}
        Icon={HomeIcon}
        menuItems={[
          {
            Icon: EditIcon,
            actions: [{ title: 'Edit Freight Order', onClick: () => setUpdateOpen(true) }],
          },
          {
            Icon: PrintIcon,
            actions: [
              {
                title: 'Print Freight Order',
                onClick: () =>
                  asyncPrint((props) => <OrderLabel {...props} order={obj.data as FreightOrder} />),
              },
              {
                title: 'Print Pick Ticket',
                onClick: () =>
                  asyncPrint((props) => <PickTicket {...props} order={obj.data as FreightOrder} />),
              },
              {
                title: 'Print Freight Order & Pick Ticket',
                onClick: () =>
                  asyncPrint((props) => (
                    <>
                      <OrderLabel order={obj.data as FreightOrder} />
                      <PickTicket {...props} order={obj.data as FreightOrder} />
                    </>
                  )),
              },
              {
                title: 'Print Freight Order Placard',
                onClick: () =>
                  asyncPrint((props) => (
                    <OrderLabel short {...props} order={obj.data as FreightOrder} />
                  )),
              },
            ],
          },
          {
            Icon: LabelIcon,
            actions: [
              {
                title: 'Generate Retailer Labels',
                onClick: () => generateRetailerLabels(obj.data.guid),
              },
              {
                title: 'Print Labels',
                onClick: () => {
                  window.dispatchEvent(
                    new CustomEvent('skuitCommand', {
                      detail: { command: 'scan', data: { scanned: obj.data } },
                    })
                  )
                  window.dispatchEvent(
                    new CustomEvent('skuitCommand', {
                      detail: { command: 'print' },
                    })
                  )
                },
              },
              {
                title: 'Set SSCC Codes',
                onClick: () => {
                  window.dispatchEvent(
                    new CustomEvent('skuitCommand', {
                      detail: { command: 'scan', data: { scanned: obj.data } },
                    })
                  )
                  window.dispatchEvent(
                    new CustomEvent('skuitCommand', {
                      detail: { command: 'setSSCC' },
                    })
                  )
                },
              },
            ],
          },
        ]}
        fields={[
          { id: 'guid', label: 'SquID', renderer: ValueField },
          { id: 'number', label: 'Number', renderer: ValueField },
          { id: 'bol', label: 'Bill of Lading', renderer: ValueField },
          { id: 'po', label: 'PO#', renderer: ValueField },
          { id: 'client', label: 'Client', renderer: RelatedLinkedField(ValueField, 'name') },
          { id: 'facility', label: 'Facility', renderer: RelatedLinkedField(ValueField, 'name') },
          { id: 'carrier', label: 'Carrier', renderer: RelatedLinkedField(ValueField, 'name') },
          { id: 'placedOn', label: 'Date Placed', renderer: DateTimeField },
          { id: 'processOn', label: 'Date to Process on', renderer: DateField },
          { id: 'fulfillOn', label: 'Date to Fulfill On', renderer: DateField },
          { id: 'cannotShipBefore', label: 'Ship On or After', renderer: DateField },
          { id: 'mustShipBy', label: 'Must Ship By', renderer: DateField },
          { id: 'expectedShipDate', label: 'Expected Ship Date', renderer: DateField },
          { id: 'mustCancelBy', label: 'Must Cancel By', renderer: DateField },
          { id: 'details', label: 'Details', renderer: ValueField },
          { id: 'retailer', label: 'Retailer', renderer: RelatedLinkedField(ValueField, 'name') },
          { id: 'retailerRoutingNumber', label: 'Retailer Routing Number', renderer: ValueField },
          { id: 'address', label: 'Address', renderer: AddressField },
          { id: 'status', label: 'Status', renderer: OrderStatusField },
          { id: 'flags', label: 'Flags', renderer: FlagsField },
          {
            id: 'facilities',
            label: 'Assigned Facilities',
            renderer: RelatedListValueField('name'),
          },
        ]}
        customFields={customFields}
      />
    </>
  )
}
